import gsap from 'gsap';

/**
 * Initializes the 3D YouTube 16:9 carousel.
 * @param {Function} openModal — callback to open the case-study modal with card data
 */
export async function initCarousel(openModal) {
  const ytRes = await fetch('/yt_videos.json');
  const YT_VIDEOS = await ytRes.json();

  const RING_ELEMENT = document.getElementById('js-carousel-ring');
  const CA_CONTAINER = document.querySelector('.carousel-container');
  const CAROUSEL_SCENE = document.querySelector('.carousel-scene');
  let carouselCards = [];
  let virtualSlots = 12;
  let theta = 30;

  // Build dynamic cards function
  function renderRing(videoArray) {
    RING_ELEMENT.innerHTML = '';
    carouselCards = [];
    virtualSlots = Math.max(12, videoArray.length);
    theta = 360 / virtualSlots;
    
    // Render exact number of videos (no duplicating) onto the virtual arc
    for (let i = 0; i < videoArray.length; i++) {
      const data = { ...videoArray[i], format: 'horizontal' };
      const card = document.createElement('div');
      card.className = 'carousel-card';
      
      if (data) {
        card.innerHTML = `
          <div class="video-spinner"></div>
          <div class="video-wrapper">
            <video loop muted playsinline preload="auto" controlsList="nodownload"><source src="${data.src}"></video>
          </div>
          <div class="video-info">
            <h3>${data.title}</h3>
            <p>${data.sub}</p>
          </div>
        `;
        const vid = card.querySelector('video');
        const spinner = card.querySelector('.video-spinner');
        if (vid) vid.addEventListener('canplay', () => spinner.classList.add('hidden'), { once: true });
        card.vidData = data;
      }
      
      RING_ELEMENT.appendChild(card);
      carouselCards.push(card);
    }
  }

  // Initial render
  renderRing(YT_VIDEOS);

  let radius = 0;
  let currentAngle = 0;
  let isDragging = false;
  let startX = 0;
  let prevX = 0;
  let velocity = 0;
  let momentumId = null;

  function setupCarousel() {
    if (!carouselCards[0]) return;
    const cardWidth = carouselCards[0].offsetWidth;
    radius = Math.round((cardWidth * 1.05) / 2 / Math.tan(Math.PI / virtualSlots));

    carouselCards.forEach((card, i) => {
      const angle = theta * i;
      card.style.transform = `rotateY(${angle}deg) translateZ(${radius}px)`;
      card.dataset.angle = angle;
    });
  }

  function updateRotations() {
    RING_ELEMENT.style.transform = `translateZ(${-radius}px) rotateY(${currentAngle}deg)`;
    const normalized = ((currentAngle % 360) + 360) % 360;
    let front = 360 - normalized;
    if (front === 360) front = 0;

    let closest = 0;
    let minDiff = 999;
    carouselCards.forEach((card, ix) => {
      const cAngle = parseFloat(card.dataset.angle);
      let diff = Math.abs(cAngle - front);
      if (diff > 180) diff = 360 - diff;
      if (diff < minDiff) {
        minDiff = diff;
        closest = ix;
      }
    });

    carouselCards.forEach((card, i) => {
      const vid = card.querySelector('video');
      if (i === closest) {
        if (!card.classList.contains('active-card')) {
          card.classList.add('active-card');
          if (vid) vid.play().catch(() => {});
        }
      } else {
        if (card.classList.contains('active-card')) {
          card.classList.remove('active-card');
          if (vid) vid.pause();
        }
      }
    });
  }

  // Pointer events
  CA_CONTAINER.addEventListener('pointerdown', (e) => {
    if (e.target.closest('.glass-modal')) return;
    isDragging = true;
    startX = e.clientX;
    prevX = e.clientX;
    velocity = 0;
    if (momentumId) cancelAnimationFrame(momentumId);
  });

  window.addEventListener('pointermove', (e) => {
    if (!isDragging) return;
    const delta = e.clientX - prevX;
    prevX = e.clientX;
    
    // Higher sensitivity on mobile since swipe travel distance is physically shorter
    const sensitivity = window.innerWidth <= 900 ? 0.15 : 0.05;
    
    let move = delta * sensitivity;
    
    // Rubber band friction if trying to drag past the available cards in an arc
    if (carouselCards.length < virtualSlots) {
       const minA = -(carouselCards.length - 1) * theta;
       if (currentAngle > 0 || currentAngle < minA) {
          move *= 0.3; 
       }
    }
    
    currentAngle += move;
    velocity = move;
    updateRotations();
  });

  window.addEventListener('pointerup', (e) => {
    if (!isDragging) return;
    isDragging = false;

    // Click detection
    const travel = Math.abs(e.clientX - startX);
    if (travel < 5) {
      const targetCard = e.target.closest('.carousel-card.active-card');
      if (targetCard && targetCard.vidData) {
        openModal(targetCard.vidData);
        return;
      }
    }

    // Momentum
    function momentum() {
      currentAngle += velocity;
      velocity *= 0.95;
      
      const isArc = carouselCards.length < virtualSlots;
      const minA = -(carouselCards.length - 1) * theta;
      let outOfBounds = false;
      
      if (isArc) {
        if (currentAngle > 0) {
          velocity *= 0.7; // Heavy friction out-of-bounds
          outOfBounds = true;
        } else if (currentAngle < minA) {
          velocity *= 0.7;
          outOfBounds = true;
        }
      }
      
      updateRotations();
      
      if (Math.abs(velocity) < 0.1 || (outOfBounds && Math.abs(velocity) < 1.0)) {
        let snap = Math.round(currentAngle / theta) * theta;
        if (isArc) {
           if (snap > 0) snap = 0;
           if (snap < minA) snap = minA;
        }
        
        gsap.to(RING_ELEMENT, {
          rotationY: snap,
          z: -radius,
          duration: 0.6,
          ease: 'power2.out',
          onUpdate: () => {
            currentAngle = snap;
            updateRotations();
          },
        });
        return;
      }
      momentumId = requestAnimationFrame(momentum);
    }
    momentum();
  });

  // Initial setup
  setTimeout(setupCarousel, 100);
  window.addEventListener('resize', setupCarousel);
  setTimeout(updateRotations, 200);

  // Dynamic Filter Generation
  const filterContainer = document.getElementById('youtube-filters');
  const uniqueCategories = new Set();
  YT_VIDEOS.forEach(v => {
    if (Array.isArray(v.category)) {
      v.category.forEach(c => uniqueCategories.add(c));
    } else if (v.category) {
      uniqueCategories.add(v.category);
    }
  });
  
  if (filterContainer && uniqueCategories.size > 0) {
    let html = '<button class="filter-btn active" data-category="All">All</button>';
    Array.from(uniqueCategories).sort().forEach(cat => {
      html += `<button class="filter-btn" data-category="${cat}">${cat}</button>`;
    });
    filterContainer.innerHTML = html;
  }

  // Filter interaction
  const filterBtns = document.querySelectorAll('#youtube-filters .filter-btn');
  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.classList.contains('active')) return; // Already selected
      
      // Update UI pills
      filterBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      
      const category = btn.dataset.category;
      let filteredData = category === 'All' 
        ? YT_VIDEOS 
        : YT_VIDEOS.filter(v => {
            if (Array.isArray(v.category)) {
              return v.category.includes(category);
            }
            return v.category === category;
          });
        
      // Fallback if empty to prevent breaking the array looping
      if (!filteredData || filteredData.length === 0) {
        filteredData = YT_VIDEOS;
      }
      
      // Stop momentum if spinning
      if (momentumId) cancelAnimationFrame(momentumId);
      isDragging = false;
      velocity = 0;

      // GSAP smooth UI transition
      gsap.to(CAROUSEL_SCENE, {
        opacity: 0,
        scale: 0.9,
        duration: 0.3,
        ease: 'power2.inOut',
        onComplete: () => {
          renderRing(filteredData);
          currentAngle = 0; 
          setupCarousel();
          updateRotations();
          
          gsap.to(CAROUSEL_SCENE, {
            opacity: 1,
            scale: 1,
            duration: 0.5,
            ease: 'back.out(1.2)'
          });
        }
      });
    });
  });

  return { CA_CONTAINER };
}
